import type paper from "paper";
import type {
  Cutout,
  Decoration,
  Hardware,
  Hole,
  Piece,
  Project,
  StitchLine,
} from "./Project";
import {
  nextCutoutName,
  nextPartName,
  nextStitchName,
  pieceForStitchLine,
  pieceHardware,
  pieceStitchLines,
} from "./Project";
import type { EditorContext } from "../tools/EditorContext";
import {
  captureConstructionFragment,
  insertConstructionFragment,
  type ConstructionFragment,
  type InsertedConstructionClones,
} from "./constructionGroupActions";
import {
  clonePieceForDuplicate,
  cloneStitchForDuplicate,
  cloneCutoutForDuplicate,
  cloneHoleForDuplicate,
  nextDuplicateDecorationName,
  mapPiece,
} from "./pieceDuplicate";
import {
  pieceDisplayOffset,
  translateDecoration,
  translatePiece,
} from "./pieceTransform";
import { newId } from "./ids";

/**
 * App clipboard for Copy / Paste (#763). Cmd+C captures the current selection
 * as a payload of full MODEL SNAPSHOTS — the project's immutable updates mean
 * the captured objects stay valid even after the originals are edited or
 * deleted, so a paste always reproduces what was copied. Cmd+V pastes with
 * the Duplicate nudge (each further paste steps one more, so repeated pastes
 * cascade like repeated Cmd+D); Cmd+F pastes IN PLACE, exactly on the
 * original. Paste inserts clones with fresh ids throughout and names that
 * continue each original's family, reusing the Duplicate machinery
 * (`clonePieceForDuplicate`, `insertConstructionFragment`, the child-feature
 * clone helpers).
 */

export type ClipboardPayload =
  | {
      kind: "pieces";
      /** Each entry keeps the piece's DISPLAYED offset at copy time (a
       *  linked part's cut-layout translate) — the clone is unlinked, so
       *  paste bakes that in to land where the original was drawn. */
      entries: Array<{ piece: Piece; displayDx: number; displayDy: number }>;
    }
  | { kind: "construction"; fragment: ConstructionFragment }
  | {
      kind: "stitches";
      entries: Array<{ ownerId: string; stitch: StitchLine }>;
    }
  | { kind: "cutout"; ownerId: string; cutout: Cutout }
  | { kind: "hole"; ownerId: string; hole: Hole; hardware: Hardware[] }
  | { kind: "decoration"; ownerId: string; decoration: Decoration };

/** Capture the current selection as a clipboard payload — child feature
 *  first (a child selection also carries its owner's `pieceId`, mirroring
 *  the Cmd+D dispatch), then whole parts, then construction objects.
 *  Null when nothing copyable is selected. */
export function copySelection(ctx: EditorContext): ClipboardPayload | null {
  const proj = ctx.history.state;
  const sel = ctx.scene.getSelection();

  const stitchIds = sel.stitchIds ?? (sel.stitchId ? [sel.stitchId] : []);
  if (stitchIds.length > 0) {
    const entries: Array<{ ownerId: string; stitch: StitchLine }> = [];
    for (const sid of stitchIds) {
      const owner = pieceForStitchLine(proj, sid);
      const stitch = owner
        ? pieceStitchLines(owner).find((s) => s.id === sid)
        : undefined;
      if (owner && stitch) entries.push({ ownerId: owner.id, stitch });
    }
    return entries.length > 0 ? { kind: "stitches", entries } : null;
  }
  if (sel.pieceId && sel.cutoutId) {
    const owner = proj.pieces.find((p) => p.id === sel.pieceId);
    const cutout = owner?.cutouts.find((c) => c.id === sel.cutoutId);
    return owner && cutout
      ? { kind: "cutout", ownerId: owner.id, cutout }
      : null;
  }
  if (sel.pieceId && sel.holeId) {
    const owner = proj.pieces.find((p) => p.id === sel.pieceId);
    const hole = owner?.holes.find((h) => h.id === sel.holeId);
    if (!owner || !hole) return null;
    // Hardware seated on the hole rides along (it has no standalone
    // selection), same as Duplicate.
    const hardware = pieceHardware(owner).filter((hw) => hw.holeId === hole.id);
    return { kind: "hole", ownerId: owner.id, hole, hardware };
  }
  if (sel.pieceId && sel.decorationId) {
    const owner = proj.pieces.find((p) => p.id === sel.pieceId);
    const decoration = owner?.decorations.find(
      (d) => d.id === sel.decorationId,
    );
    return owner && decoration
      ? { kind: "decoration", ownerId: owner.id, decoration }
      : null;
  }

  const pieceIds = sel.pieceIds ?? (sel.pieceId ? [sel.pieceId] : []);
  if (pieceIds.length > 0) {
    const view = ctx.scene.getPartLayoutView();
    const idSet = new Set(pieceIds);
    const entries = proj.pieces
      .filter((p) => idSet.has(p.id))
      .map((p) => {
        const off = pieceDisplayOffset(p, view);
        return { piece: p, displayDx: off.dx, displayDy: off.dy };
      });
    return entries.length > 0 ? { kind: "pieces", entries } : null;
  }

  const fragment = captureConstructionFragment(proj, sel);
  return fragment ? { kind: "construction", fragment } : null;
}

/** Paste a payload into the current project: fresh ids throughout, names
 *  continuing each original's family, geometry offset by `offsetMm` on
 *  both axes (0 = paste in place). The clones become the selection.
 *  Returns false when nothing could be pasted (e.g. a child feature whose
 *  owner part no longer exists). */
export function pasteClipboard(
  ctx: EditorContext,
  payload: ClipboardPayload,
  opts: { offsetMm: number },
): boolean {
  const { offsetMm } = opts;
  const scope = ctx.scene.getPaperScope();

  switch (payload.kind) {
    case "pieces": {
      const newIds: string[] = [];
      ctx.commit("Paste part", (proj) => {
        let next = proj;
        for (const { piece, displayDx, displayDy } of payload.entries) {
          const name = nextPartName(next);
          const clone = translatePiece(
            clonePieceForDuplicate(piece, name),
            displayDx + offsetMm,
            displayDy + offsetMm,
            scope,
          );
          newIds.push(clone.id);
          next = { ...next, pieces: [...next.pieces, clone] };
        }
        return next;
      });
      if (newIds.length === 0) return false;
      ctx.setSelection({
        pieceId: newIds[newIds.length - 1] ?? null,
        pieceIds: newIds,
        decorationId: null,
        holeId: null,
        cutoutId: null,
        stitchId: null,
        constructionIds: [],
        constructionGroupIds: [],
        tracingImageIds: [],
      });
      return true;
    }

    case "construction": {
      let inserted: InsertedConstructionClones | null = null;
      ctx.commit("Paste construction", (proj0) => {
        inserted = insertConstructionFragment(
          proj0,
          payload.fragment,
          offsetMm,
          scope,
        );
        return inserted.project;
      });
      if (!inserted) return false;
      const done: InsertedConstructionClones = inserted;
      ctx.setSelection(
        { pieceId: null, decorationId: null, ...done.selection },
        { autoPromoteGroups: false },
      );
      return true;
    }

    case "stitches": {
      const cloned: Array<{ ownerId: string; id: string }> = [];
      ctx.commit("Paste stitch line", (proj) => {
        let next = proj;
        for (const { ownerId, stitch } of payload.entries) {
          if (!next.pieces.some((p) => p.id === ownerId)) continue;
          const clone = cloneStitchForDuplicate(
            stitch,
            offsetMm,
            offsetMm,
            nextStitchName(next),
          );
          cloned.push({ ownerId, id: clone.id });
          next = mapPiece(next, ownerId, (pc) => ({
            ...pc,
            stitchLines: [...pieceStitchLines(pc), clone],
          }));
        }
        return next;
      });
      if (cloned.length === 0) return false;
      const newIds = cloned.map((c) => c.id);
      ctx.setSelection({
        pieceId: cloned[cloned.length - 1]!.ownerId,
        decorationId: null,
        holeId: null,
        cutoutId: null,
        stitchId: newIds[newIds.length - 1] ?? null,
        stitchIds: newIds,
      });
      return true;
    }

    case "cutout": {
      let newCutoutId: string | null = null;
      ctx.commit("Paste cutout", (proj) => {
        const owner = proj.pieces.find((p) => p.id === payload.ownerId);
        if (!owner) return proj;
        const clone = cloneCutoutForDuplicate(
          payload.cutout,
          offsetMm,
          offsetMm,
          scope,
          nextCutoutName(owner),
        );
        newCutoutId = clone.id;
        return mapPiece(proj, owner.id, (pc) => ({
          ...pc,
          cutouts: [...pc.cutouts, clone],
        }));
      });
      if (!newCutoutId) return false;
      ctx.setSelection({
        pieceId: payload.ownerId,
        decorationId: null,
        holeId: null,
        stitchId: null,
        cutoutId: newCutoutId,
      });
      return true;
    }

    case "hole": {
      let newHoleId: string | null = null;
      ctx.commit("Paste hole", (proj) => {
        const owner = proj.pieces.find((p) => p.id === payload.ownerId);
        if (!owner) return proj;
        const clone = cloneHoleForDuplicate(payload.hole, offsetMm, offsetMm);
        newHoleId = clone.id;
        const clonedHw = payload.hardware.map((hw) => ({
          ...hw,
          id: newId("hw"),
          holeId: clone.id,
        }));
        return mapPiece(proj, owner.id, (pc) => ({
          ...pc,
          holes: [...pc.holes, clone],
          hardware:
            clonedHw.length > 0
              ? [...pieceHardware(pc), ...clonedHw]
              : pc.hardware,
        }));
      });
      if (!newHoleId) return false;
      ctx.setSelection({
        pieceId: payload.ownerId,
        decorationId: null,
        cutoutId: null,
        stitchId: null,
        holeId: newHoleId,
      });
      return true;
    }

    case "decoration": {
      let newDecorationId: string | null = null;
      ctx.commit("Paste decoration", (proj) => {
        const owner = proj.pieces.find((p) => p.id === payload.ownerId);
        if (!owner) return proj;
        const moved: Decoration = translateDecoration(
          payload.decoration,
          offsetMm,
          offsetMm,
          scope,
        );
        const clone: Decoration = {
          ...moved,
          id: newId("dec"),
          name: nextDuplicateDecorationName(owner, payload.decoration.name),
        };
        newDecorationId = clone.id;
        return mapPiece(proj, owner.id, (pc) => ({
          ...pc,
          decorations: [...pc.decorations, clone],
        }));
      });
      if (!newDecorationId) return false;
      ctx.setSelection({
        pieceId: payload.ownerId,
        decorationId: newDecorationId,
        holeId: null,
        cutoutId: null,
        stitchId: null,
      });
      return true;
    }
  }
}
